import { inject } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { CanActivateChildFn, CanActivateFn, Router } from '@angular/router';
import { PAPEIS_OPERADOR_ESIC, PAPEIS_OPERADOR_OUVIDORIA } from '@core/auth/auth.models';
import { AuthService } from '@core/auth/auth.service';
import { EntidadeContextoService } from '@core/contexto/entidade-contexto.service';

/**
 * Protege as rotas que exigem sessão. Sem token vigente, redireciona para a tela de login
 * preservando a URL de retorno.
 *
 * A checagem é `auth.token()`, e não o signal `isAuthenticated`: só ela reavalia a expiração no
 * momento da navegação (e descarta o token vencido). Um signal memoizado poderia devolver o
 * `true` calculado antes de o token expirar, deixando a tela abrir sem sessão — foi assim que um
 * token vencido chegou a renderizar o formulário inteiro e só falhar no envio.
 *
 * O login de destino sai de `data.loginUrl` quando a rota o declara; caso contrário é deduzido do
 * módulo na própria URL, para que o cidadão do e-SIC não caia no login da Ouvidoria.
 */
export const authGuard: CanActivateFn = (route, state) => {
  const auth = inject(AuthService);
  const router = inject(Router);

  if (auth.token() !== null) {
    return true;
  }
  const loginUrl = (route.data['loginUrl'] as string | undefined) ?? loginDoModulo(state.url);
  return router.createUrlTree(
    rotaDeLogin(loginUrl, inject(EntidadeContextoService).idTenancy()),
    { queryParams: { returnUrl: state.url } });
};

/** Login do módulo a que a URL pertence; na dúvida, o do e-SIC, que é a raiz do produto. */
export function loginDoModulo(url: string): string {
  return url.startsWith('/modulo/ouvidoria')
    ? '/modulo/ouvidoria/login'
    : '/modulo/esic/login';
}

/**
 * Comandos de rota para uma tela de login, com a entidade quando ela é conhecida — ex.:
 * `['/modulo/ouvidoria/login', 6666666]`.
 *
 * Existe para que a sessão que termina (por logout ou por expirar) não faça o usuário escolher a
 * entidade de novo ao reentrar. Nas guardas a entidade só pode vir do
 * {@link EntidadeContextoService}: elas redirecionam justamente porque não há mais token de onde
 * ler o claim, e o contexto sobrevive ao logout, que só apaga o token.
 *
 * Sem entidade conhecida devolve só o login — melhor pedi-la de novo do que navegar para uma URL
 * terminada em "null".
 */
export function rotaDeLogin(login: string, idTenancy: number | null): unknown[] {
  return idTenancy != null ? [login, idTenancy] : [login];
}

/**
 * Restringe o ramo administrativo do e-SIC (`/modulo/esic/admin/**`) aos operadores —
 * {@link PAPEIS_OPERADOR_ESIC}: ROLE_ESIC_GESTOR, ROLE_ESIC_CADASTRADOR e ROLE_ESIC_RESPONDENTE.
 *
 * Aplicado como `canActivateChild` do shell `modulo`: roda em toda navegação filha, mas só
 * decide nas URLs administrativas do e-SIC — as demais (cidadão, Ouvidoria) passam direto.
 * Sem sessão, o redirect de login com `returnUrl` é o mesmo do {@link authGuard}; autenticado
 * sem papel de operador, o usuário (cidadão) vai para o painel do cidadão com o aviso.
 *
 * Como toda checagem de papel no cliente, isto é navegação/UI: a autorização real continua nos
 * `@PreAuthorize` do backend — sem o papel, as APIs administrativas respondem 403 de todo modo.
 */
export const esicAdminGuard: CanActivateChildFn = (route, state) => {
  if (!state.url.startsWith('/modulo/esic/admin')) {
    return true;
  }

  const auth = inject(AuthService);
  const router = inject(Router);

  if (auth.token() === null) {
    return router.createUrlTree(
      rotaDeLogin('/modulo/esic/login', inject(EntidadeContextoService).idTenancy()),
      { queryParams: { returnUrl: state.url } });
  }
  if (auth.hasAlgumRole(PAPEIS_OPERADOR_ESIC)) {
    return true;
  }

  inject(MatSnackBar).open(
    'Você não tem permissão para acessar a área administrativa do e-SIC.', 'OK', { duration: 5000 });
  return router.createUrlTree(['/modulo/esic/cidadao/principal']);
};

/**
 * Papéis que dão acesso à área do cidadão do e-SIC: o próprio cidadão e os operadores — o
 * servidor também é um usuário do e-SIC e o legado o deixava transitar pelas telas públicas
 * (`SecuredEsic` só exigia sessão).
 */
const PAPEIS_ESIC_CIDADAO = [...PAPEIS_OPERADOR_ESIC, 'ROLE_CIDADAO'] as const;

/**
 * Restringe o ramo do cidadão do e-SIC (`/modulo/esic/cidadao/**`) a quem tem algum papel do
 * e-SIC — {@link PAPEIS_ESIC_CIDADAO}. Barra, por exemplo, um operador só da Ouvidoria.
 *
 * Mesmo desenho do {@link esicAdminGuard}: `canActivateChild` do shell `modulo`, decidindo só
 * nas URLs deste prefixo. Checagem de cliente para navegação/UI — a autorização real é dos
 * `@PreAuthorize` e das guardas de ownership do backend.
 */
export const esicCidadaoGuard: CanActivateChildFn = (route, state) => {
  if (!state.url.startsWith('/modulo/esic/cidadao')) {
    return true;
  }

  const auth = inject(AuthService);
  const router = inject(Router);

  if (auth.token() === null) {
    return router.createUrlTree(
      rotaDeLogin('/modulo/esic/login', inject(EntidadeContextoService).idTenancy()),
      { queryParams: { returnUrl: state.url } });
  }
  if (auth.hasAlgumRole(PAPEIS_ESIC_CIDADAO)) {
    return true;
  }

  inject(MatSnackBar).open(
    'Você não tem permissão para acessar a área do cidadão do e-SIC.', 'OK', { duration: 5000 });
  return router.createUrlTree(['/']);
};

/**
 * Restringe o ramo administrativo da Ouvidoria (`/modulo/ouvidoria/admin/**`) aos operadores —
 * {@link PAPEIS_OPERADOR_OUVIDORIA}: gestor (ADMIN), CADASTRADOR e atendimento (OPERADOR).
 * Mesmo desenho do {@link esicAdminGuard}; a autorização real continua no backend.
 */
export const ouvidoriaAdminGuard: CanActivateChildFn = (route, state) => {
  if (!state.url.startsWith('/modulo/ouvidoria/admin')) {
    return true;
  }

  const auth = inject(AuthService);
  const router = inject(Router);

  if (auth.token() === null) {
    return router.createUrlTree(
      rotaDeLogin('/modulo/ouvidoria/login', inject(EntidadeContextoService).idTenancy()),
      { queryParams: { returnUrl: state.url } });
  }
  if (auth.hasAlgumRole(PAPEIS_OPERADOR_OUVIDORIA)) {
    return true;
  }

  inject(MatSnackBar).open(
    'Você não tem permissão para acessar a área administrativa da Ouvidoria.', 'OK', { duration: 5000 });
  return router.createUrlTree(['/modulo/ouvidoria/cidadao/principal']);
};

/** Papéis que dão acesso à área do cidadão da Ouvidoria — mesmo critério do e-SIC. */
const PAPEIS_OUVIDORIA_CIDADAO = [...PAPEIS_OPERADOR_OUVIDORIA, 'ROLE_CIDADAO'] as const;

/**
 * Restringe o ramo do cidadão da Ouvidoria (`/modulo/ouvidoria/cidadao/**`) a quem tem algum
 * papel do módulo — {@link PAPEIS_OUVIDORIA_CIDADAO}. Mesmo desenho do {@link esicCidadaoGuard}.
 */
export const ouvidoriaCidadaoGuard: CanActivateChildFn = (route, state) => {
  if (!state.url.startsWith('/modulo/ouvidoria/cidadao')) {
    return true;
  }

  const auth = inject(AuthService);
  const router = inject(Router);

  if (auth.token() === null) {
    return router.createUrlTree(
      rotaDeLogin('/modulo/ouvidoria/login', inject(EntidadeContextoService).idTenancy()),
      { queryParams: { returnUrl: state.url } });
  }
  if (auth.hasAlgumRole(PAPEIS_OUVIDORIA_CIDADAO)) {
    return true;
  }

  inject(MatSnackBar).open(
    'Você não tem permissão para acessar a área do cidadão da Ouvidoria.', 'OK', { duration: 5000 });
  return router.createUrlTree(['/']);
};
